import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaCoins, FaShieldAlt, FaBalanceScale, FaHandHoldingHeart, FaArrowRight, FaExternalLinkAlt } from 'react-icons/fa';
import './Stake.scss';
import Header from '../Header';
import Footer from '../Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import stakeMascotImage from '../../../assets/radixandgreenmonster.png';
import { RSWAP_STAKING_URL, RSWAP_STAKING_WIDGET_URL } from '../../../config/siteContent';

const WEEKLY_DCKS_PAYOUT = 48300;
const weeklyPayoutLabel = WEEKLY_DCKS_PAYOUT.toLocaleString();

// Scroll-triggered reveal hook (same pattern as homepage)
const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const observe = useCallback(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return observer;
  }, [threshold]);

  useEffect(() => {
    const observer = observe();
    return () => observer?.disconnect();
  }, [observe]);

  return [ref, isVisible];
};

const Stake = () => {
  const [stakingRef, stakingVisible] = useScrollReveal(0.08);
  const [benefitsRef, benefitsVisible] = useScrollReveal(0.05);
  const [widgetRef, widgetVisible] = useScrollReveal(0.08);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="stake-page" data-scroll-root>
      <Header />

      <main className="stake-content">
        {/* ============ HERO SECTION ============ */}
        <section className="stake-hero">
          <div className="stake-hero__bg-effects">
            <div className="stake-hero__orb stake-hero__orb--1"></div>
            <div className="stake-hero__orb stake-hero__orb--2"></div>
            <div className="stake-hero__orb stake-hero__orb--3"></div>
          </div>
          <div className="container">
            <div className="stake-hero__content">
              <p className="stake-hero__tagline">Stake on RSwap</p>
              <h1 className="stake-hero__title">
                <span className="stake-hero__title-line">Stake Your</span>
                <span className="stake-hero__title-brand">$DCKS</span>
              </h1>
              <p className="stake-hero__subtitle">
                Stake DCKS on RSwap and get your share of {weeklyPayoutLabel} DCKS paid out each week.
              </p>
              <a href="#stake-widget" className="stake-hero__cta">
                Stake on RSwap <FaArrowRight />
              </a>
            </div>
          </div>
          <div className="stake-hero__scroll-indicator">
            <span></span>
          </div>
        </section>

        {/* ============ WHAT IS STAKING ============ */}
        <section ref={stakingRef} className={`stake-info ${stakingVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <div className="stake-info__grid">
              <div className="stake-info__card">
                <div className="stake-info__card-accent"></div>
                <div className="stake-info__card-content">
                  <h2 className="section-title">What is Staking $DCKS?</h2>
                  <p>Staking DCKS on RSwap is simple: lock your tokens, keep them working, and earn a share of the weekly payout pool.</p>
                  <p>Each week, <strong>{weeklyPayoutLabel} DCKS</strong> is paid out to stakers. Your share depends on how much DCKS you have staked compared with the rest of the pool. You can unstake whenever you want.</p>
                  <p>This page is for DCKS staking. To stake XRD with the REDDICKS validator, go to the <Link to="/validator">validator page</Link>.</p>
                  <div className="stake-plain-language">
                    <h3>Plain-Language Payout Summary</h3>
                    <ul>
                      <li>Stake DCKS on RSwap.</li>
                      <li>{weeklyPayoutLabel} DCKS is paid out every week.</li>
                      <li>You receive your share of that weekly pool.</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="stake-info__mascot">
                <div className="stake-info__mascot-glow"></div>
                <img src={stakeMascotImage} alt="Reddicks Staking Meme" className="stake-info__mascot-img" />
              </div>
            </div>
          </div>
        </section>

        {/* ============ BENEFITS SECTION ============ */}
        <section ref={benefitsRef} className={`stake-benefits ${benefitsVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <div className="stake-benefits__header">
              <span className="stake-benefits__tag">Benefits</span>
              <h2 className="section-title centered">Why Stake Your $DCKS?</h2>
            </div>
            <div className="stake-benefits__grid">
              <div className="stake-benefit-card">
                <div className="stake-benefit-card__icon stake-benefit-card__icon--crimson">
                  <FaCoins />
                </div>
                <h3>{weeklyPayoutLabel} DCKS Every Week</h3>
                <p>A fixed {weeklyPayoutLabel} DCKS is paid out each week and split among everyone staking on RSwap.</p>
              </div>

              <div className="stake-benefit-card">
                <div className="stake-benefit-card__icon stake-benefit-card__icon--violet">
                  <FaBalanceScale />
                </div>
                <h3>Your Share of the Pool</h3>
                <p>Rewards are proportional. Stake more DCKS, and you receive a larger share of the weekly {weeklyPayoutLabel} DCKS payout.</p>
              </div>

              <div className="stake-benefit-card">
                <div className="stake-benefit-card__icon stake-benefit-card__icon--cyan">
                  <FaShieldAlt />
                </div>
                <h3>Unstake Anytime</h3>
                <p>Your DCKS stays yours. Stake on RSwap when you want in, and unstake whenever you want out.</p>
              </div>

              <div className="stake-benefit-card">
                <div className="stake-benefit-card__icon stake-benefit-card__icon--coral">
                  <FaHandHoldingHeart />
                </div>
                <h3>Support the Project</h3>
                <p>Staking DCKS on RSwap helps keep the ecosystem active and shows long-term support for REDDICKS.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ============ STAKING WIDGET ============ */}
        <section ref={widgetRef} id="stake-widget" className={`stake-widget ${widgetVisible ? 'visible' : ''}`}>
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Stake DCKS on RSwap</h2>
            <p className="stake-widget__intro">
              Connect your Radix wallet, stake DCKS on RSwap, and start earning your share of {weeklyPayoutLabel} DCKS each week.
            </p>
            <div className="stake-widget__container">
              <iframe
                title="RSwap DCKS staking"
                src={RSWAP_STAKING_WIDGET_URL}
                width="100%"
                scrolling="no"
                height="1000px"
                style={{ border: 0, margin: '0 auto', display: 'block', maxWidth: '960px', minWidth: '375px', colorScheme: 'normal' }}
              />
            </div>
            <a
              href={RSWAP_STAKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="stake-widget__external"
            >
              Open staking on RSwap <FaExternalLinkAlt />
            </a>
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Stake;
